import ProgressIndicatorContainer from "components/ProgressIndicatorContainer";
import { SORT_TYPES } from "components/SharedTable/sharedSortTypes";
import {
  CSSProperties,
  MouseEvent,
  MutableRefObject,
  useEffect,
  useMemo
} from "react";
import {
  Column,
  Row,
  SortingRule,
  useRowSelect,
  useSortBy,
  useTable
} from "react-table";
import { styled } from "@mui/material/styles";

import {
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip
} from "@mui/material";
import { useLoadCounter } from "hooks/useLoadCounter";
import { withTransientProps } from "core/utils/transientProps";

export interface SortByConfig {
  id: string;
  desc: boolean;
}

export interface BasicTableProps<T extends object> {
  columns: Column<T>[];
  data: T[];
  dataQa: string;
  loading?: boolean;
  stickyHeader?: boolean;
  emptyMessage?: string;
  onClickRow?: (row: Row<T>, event: MouseEvent<HTMLElement>) => void;
  isRowSelected?: (obj: T) => boolean;
  defaultSortBy?: SortByConfig[];
  rowsRef?: MutableRefObject<(HTMLTableRowElement | null)[]>;
}

/**
 * Basic Table is a simple table with no sorting, filtering or pagination.
 * It uses the default MaterialUI styling, and uses React Table to provide
 * rendering.
 *
 * @template T - a generic set of objects that are to be displayed in the table e.g. {a : number, b: string}
 * @param columns - React Table definition of columns e.g. [{ name: "A", accessor: "a"}, { name "B", accessor: "b"}]
 * @param data - an array of data of type T e.g. [{"a": 1, "b": "Hello"}]
 * @param onClickRow - a method that takes object of type Row<T> and provides on-click behaviour for the whole row.
 * @param selectedRows - an optional array of the row's to be selected by ID. Count from 1.
 */
export function BasicTable<T extends object>({
  columns,
  data,
  dataQa,
  loading,
  stickyHeader = true,
  emptyMessage = "No data to display",
  isRowSelected = () => false,
  onClickRow
}: BasicTableProps<T>): JSX.Element {
  const tableLoadCount = useLoadCounter(loading);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow
  } = useTable({ columns, data }, useSortBy, useRowSelect);

  const hasData = data.length > 0;

  const isSelected = (row: Row<T>): boolean => {
    return isRowSelected(row.original);
  };

  return (
    <Table
      data-qa={dataQa}
      stickyHeader={stickyHeader}
      size="small"
      {...getTableProps()}
    >
      <TableHead>
        {headerGroups.map(headerGroup => (
          <TableRow {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column, i) => (
              <StyledHeaderCell data-col={i + 1} {...column.getHeaderProps()}>
                {column.render("Header")}
              </StyledHeaderCell>
            ))}
          </TableRow>
        ))}
      </TableHead>
      <TableBody
        data-qa_loading={loading}
        data-qa_load_count={tableLoadCount}
        {...getTableBodyProps()}
      >
        {loading && (
          <TableRow>
            <StyledTableCell colSpan={columns.length} align="center">
              <ProgressIndicatorContainer loading={loading} />
            </StyledTableCell>
          </TableRow>
        )}

        {!loading && !hasData && (
          <TableRow>
            <StyledTableCell colSpan={columns.length} align="center">
              {emptyMessage}
            </StyledTableCell>
          </TableRow>
        )}

        {!loading &&
          hasData &&
          rows.map(row => {
            prepareRow(row);
            return (
              <ClickableTableRow
                hover
                {...row.getRowProps()}
                onClick={(event: React.MouseEvent<HTMLTableRowElement>) =>
                  onClickRow ? onClickRow(row, event) : undefined
                }
                selected={isSelected(row)}
                $clickable={Boolean(onClickRow)}
              >
                {row.cells.map((cell, i) => (
                  <StyledTableCell data-col={i + 1} {...cell.getCellProps()}>
                    {cell.render("Cell")}
                  </StyledTableCell>
                ))}
              </ClickableTableRow>
            );
          })}
      </TableBody>
    </Table>
  );
}

export interface SortableTableProps<T extends object>
  extends BasicTableProps<T> {}

/**
 * Sortable Table is a simple table with sorting, and no filtering or pagination.
 * It uses the default MaterialUI styling, and uses React Table to provide
 * rendering.
 *
 * @template T - a generic set of objects that are to be displayed in the table.
 * @param columns - React Table definition of columns e.g. [{ name: "A", accessor: "a"}].
 * @param data - an array of data of type T e.g. [{"a": 1, "b": 2}].
 * @param dataQa - a name that uniquely identifies the table for reference by tests.
 * @param loading - Loading indicator.
 * @param stickyHeader - Turn on and off sticky header.
 * @param emptyMessage - A text for no data row.
 * @param isRowSelected - A flag telling whether the row is selected or not.
 * @param onClickRow - a method that takes object of type Row<T> and provides on-click behaviour for the whole row.
 * @param rowsRef - Reference information for scrolling effect.
 */
export function SortableTable<T extends object>({
  columns,
  data,
  dataQa,
  loading,
  stickyHeader = true,
  emptyMessage = "No data to display",
  isRowSelected = () => false,
  onClickRow,
  rowsRef
}: SortableTableProps<T>): JSX.Element {
  const tableLoadCount = useLoadCounter(loading);
  const sortTypes = useMemo(() => SORT_TYPES, []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow
  } = useTable(
    {
      columns,
      data,
      sortTypes
    },
    useSortBy
  );

  const hasData = data.length > 0;

  const isSelected = (row: Row<T>): boolean => {
    return isRowSelected(row.original);
  };

  const numberOfSortedColumns = headerGroups[0]?.headers.filter(
    col => col.isSorted
  ).length;

  return (
    <>
      <Table
        data-qa={dataQa}
        stickyHeader={stickyHeader}
        size="small"
        {...getTableProps()}
      >
        <TableHead>
          {headerGroups.map(headerGroup => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, i) => (
                <StyledHeaderCell
                  data-col={i + 1}
                  {...column.getHeaderProps(
                    column.getSortByToggleProps({ title: undefined })
                  )}
                  {...column.getHeaderProps(
                    column.getSortByToggleProps({ title: undefined })
                  )}
                  {...column.getHeaderProps(
                    column.getSortByToggleProps({ title: undefined })
                  )}
                >
                  <HeaderContentWrapper>
                    {column.render("Header")}
                    {!column.disableSortBy && (
                      <Tooltip
                        title={
                          <MultiSortColumnTooltipTitle>
                            Multi-sort: Hold <kbd>Shift</kbd>
                          </MultiSortColumnTooltipTitle>
                        }
                      >
                        <TableSortLabel
                          active={column.isSorted}
                          // Column sorting direction is reversed in aihub by choice
                          direction={column?.isSortedDesc ? "desc" : "asc"}
                        />
                      </Tooltip>
                    )}
                    {column.isSorted && numberOfSortedColumns > 1 && (
                      <SortIndex>{column.sortedIndex + 1}</SortIndex>
                    )}
                  </HeaderContentWrapper>
                </StyledHeaderCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody
          data-qa_loading={loading}
          data-qa_load_count={tableLoadCount}
          {...getTableBodyProps()}
        >
          {loading && (
            <TableRow>
              <StyledTableCell colSpan={columns.length} align="center">
                <ProgressIndicatorContainer loading={loading} />
              </StyledTableCell>
            </TableRow>
          )}

          {!loading && !hasData && (
            <TableRow>
              <StyledTableCell colSpan={columns.length} align="center">
                {emptyMessage}
              </StyledTableCell>
            </TableRow>
          )}

          {!loading &&
            hasData &&
            rows.map((row, index) => {
              prepareRow(row);
              return (
                <ClickableTableRow
                  hover
                  {...row.getRowProps()}
                  ref={(element: HTMLTableRowElement) =>
                    rowsRef && (rowsRef.current[index] = element)
                  }
                  onClick={
                    onClickRow
                      ? (event: React.MouseEvent<HTMLTableRowElement>) =>
                          onClickRow(row, event)
                      : undefined
                  }
                  selected={isSelected(row)}
                  $clickable={Boolean(onClickRow)}
                >
                  {row.cells.map((cell, i) => (
                    <StyledTableCell data-col={i + 1} {...cell.getCellProps()}>
                      {cell.render("Cell")}
                    </StyledTableCell>
                  ))}
                </ClickableTableRow>
              );
            })}
        </TableBody>
      </Table>
    </>
  );
}

export interface PaginatedTableProps<T extends object>
  extends SortableTableProps<T> {
  count: number;
  page: number;
  pageSize: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => void;
  onSortingChange: (sortBy: SortingRule<T>[]) => void;
  onPageSizeChange: (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  onRowSelectionChange?: (selectedRows: T[]) => void;
  selectedRows?: T[];
  showSelectionColumn?: boolean;
  canSelectRow?: (row: T) => boolean;
}

/**
 * PaginatedTable is a simple table with pagination and sorting. Sorting and paginated *must*
 * be handled by the consumer, since the sorting must be done from the full data set when
 * pagination is being used.
 *
 * @template T - a generic set of objects that are to be displayed in the table.
 * @param columns - React Table definition of columns e.g. [{ name: "A", accessor: "a"}].
 * @param data - an array of data of type T e.g. [{"a": 1, "b": 2}].
 * @param dataQa - a name that uniquely identifies the table for reference by tests.
 * @param loading - Loading indicator.
 * @param stickyHeader - Turn on and off sticky header.
 * @param count - total number of objects that exist.
 * @param page - current page.
 * @param pageSize - total page size.
 * @param onPageChange - method to fetch data for the next page.
 * @param onSortingChange - a method when sorting is changed.
 * @param onPageSizeChange - method to refetch data when the page size changes.
 * @param emptyMessage - A text for no data row.
 * @param isRowSelected - A flag telling whether the row is selected or not.
 * @param onClickRow - a method that takes object of type Row<T> and provides on-click behaviour for the whole row.
 * @param defaultSortBy - list of default sort data.
 * @param onRowSelectionChange - function to call when the row selection changes e.g. when a user selects or deselects a row.
 * @param selectedRows - list of rows that are selected.
 * @param showSelectionColumn - when true the first column will contain checkboxes to allow the user to select rows.
 * @param canSelectRow - function used to determine whether each row can be selected or not.
 *
 * TODO: Make sorting function a required argument.
 */
export function PaginatedTable<T extends object>({
  columns,
  data,
  dataQa,
  loading,
  stickyHeader = true,
  count,
  page,
  pageSize,
  emptyMessage = "No data to display",
  onPageSizeChange,
  onPageChange,
  onSortingChange,
  isRowSelected = () => false,
  onClickRow,
  onRowSelectionChange,
  selectedRows,
  defaultSortBy = [],
  showSelectionColumn = false,
  canSelectRow = undefined
}: PaginatedTableProps<T>): JSX.Element {
  const tableLoadCount = useLoadCounter(loading);
  const sortTypes = useMemo(() => SORT_TYPES, []);

  const isSelected = (row: Row<T>): boolean => {
    return (
      !!selectedRows?.includes(row.original) || isRowSelected(row.original)
    );
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { sortBy }
  } = useTable(
    {
      columns,
      data,
      sortTypes,
      manualSortBy: true,
      initialState: { sortBy: defaultSortBy }
    },
    useSortBy
  );

  const onSelectRowChange = (
    ev: React.ChangeEvent<HTMLInputElement>,
    row: T
  ): void => {
    if (!onRowSelectionChange) return;
    let newSelectedRows = [];

    if (selectedRows?.includes(row)) {
      newSelectedRows = selectedRows.filter(sr => sr !== row);
    } else {
      newSelectedRows = [...(selectedRows || []), row];
    }

    onRowSelectionChange(newSelectedRows);
  };

  const hasData = data.length > 0;

  // When the sortBy state changes (through a user interaction) we need to manually call the setSortBy
  // method supplied by the table author.
  useEffect(() => {
    onSortingChange(sortBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy]);

  const numberOfSortedColumns = headerGroups[0]?.headers.filter(
    col => col.isSorted
  ).length;

  const getCellStyle = (columnIndex: number): CSSProperties | undefined => {
    const styleObj: CSSProperties = {};
    if (typeof columns[columnIndex].width !== "undefined") {
      styleObj.width = columns[columnIndex].width;
    }
    return Object.keys(styleObj).length > 0 ? styleObj : undefined;
  };

  return (
    <PaginatedTableWrapper>
      <TableContainer>
        <Table
          data-qa={dataQa}
          stickyHeader={stickyHeader}
          size="small"
          {...getTableProps()}
        >
          <TableHead>
            {headerGroups.map(headerGroup => (
              <TableRow {...headerGroup.getHeaderGroupProps()}>
                {showSelectionColumn && (
                  <StyledSelectionHeaderCell></StyledSelectionHeaderCell>
                )}
                {headerGroup.headers.map((column, i) => (
                  <StyledHeaderCell
                    data-col={i + 1}
                    {...column.getHeaderProps(
                      column.getSortByToggleProps({ title: undefined })
                    )}
                  >
                    <HeaderContentWrapper>
                      {column.render("Header")}
                      {!column.disableSortBy && (
                        <Tooltip
                          title={
                            <MultiSortColumnTooltipTitle>
                              Multi-sort: Hold <kbd>Shift</kbd>
                            </MultiSortColumnTooltipTitle>
                          }
                        >
                          <TableSortLabel
                            active={column.isSorted}
                            // Column sorting direction is reversed in aihub by choice
                            direction={column?.isSortedDesc ? "desc" : "asc"}
                          />
                        </Tooltip>
                      )}

                      {column.isSorted && numberOfSortedColumns > 1 && (
                        <SortIndex>{column.sortedIndex + 1}</SortIndex>
                      )}
                    </HeaderContentWrapper>
                  </StyledHeaderCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody
            data-qa_loading={loading}
            data-qa_load_count={tableLoadCount}
            {...getTableBodyProps()}
          >
            {loading && (
              <TableRow>
                <StyledTableCell
                  colSpan={
                    showSelectionColumn ? columns.length + 1 : columns.length
                  }
                  align="center"
                >
                  <ProgressIndicatorContainer loading={loading} />
                </StyledTableCell>
              </TableRow>
            )}

            {!loading && !hasData && (
              <TableRow>
                <StyledTableCell
                  colSpan={
                    showSelectionColumn ? columns.length + 1 : columns.length
                  }
                  align="center"
                >
                  {emptyMessage}
                </StyledTableCell>
              </TableRow>
            )}

            {!loading &&
              hasData &&
              rows.map(row => {
                prepareRow(row);
                return (
                  <ClickableTableRow
                    hover
                    {...row.getRowProps()}
                    onClick={
                      onClickRow
                        ? (event: React.MouseEvent<HTMLTableRowElement>) =>
                            onClickRow(row, event)
                        : undefined
                    }
                    selected={isSelected(row)}
                    $clickable={Boolean(onClickRow)}
                  >
                    {showSelectionColumn && (
                      <StyledTableCell role="cell">
                        <Checkbox
                          disabled={
                            canSelectRow !== undefined &&
                            !canSelectRow(row.original)
                          }
                          checked={isSelected(row)}
                          onChange={(ev: React.ChangeEvent<HTMLInputElement>) =>
                            onSelectRowChange(ev, row.original)
                          }
                        />
                      </StyledTableCell>
                    )}
                    {row.cells.map((cell, i) => (
                      <StyledTableCell
                        data-col={i + 1}
                        style={getCellStyle(i)}
                        {...cell.getCellProps()}
                      >
                        {cell.render("Cell")}
                      </StyledTableCell>
                    ))}
                  </ClickableTableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <PaginationContainer>
        <TablePagination
          component="div"
          count={count}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={pageSize}
          onRowsPerPageChange={onPageSizeChange}
        />
      </PaginationContainer>
    </PaginatedTableWrapper>
  );
}

const PaginatedTableWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
  min-height: 100%;
`;

const SortIndex = styled("div")`
  font-size: 70%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #009f33;
  color: white;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
`;

const PaginationContainer = styled("div")`
  flex-shrink: 0;
`;

const StyledHeaderCell = styled(TableCell)`
  padding: 8px 8px 8px 8px;
  font-weight: bold;
  text-align: left;
  vertical-align: top;
  background-color: #ffffff;
  user-select: none;
`;

const StyledSelectionHeaderCell = styled(StyledHeaderCell)`
  width: 50px;
`;

const ClickableTableRow = styled(TableRow, withTransientProps)<{
  $clickable: boolean;
}>`
  cursor: ${props => (props.$clickable ? "pointer" : "unset")};
`;

const StyledTableCell = styled(TableCell)`
  padding: 8px 8px 8px 8px;
`;

const HeaderContentWrapper = styled("div")`
  display: flex;
  align-items: flex-start;
`;

const MultiSortColumnTooltipTitle = styled("div")`
  font-weight: normal;
`;
